import { useState, type FormEvent } from "react";
import { Heart, LockKeyhole, Sparkles, UserRound } from "lucide-react";
import { api } from "../api";
import type { User } from "../auth";
import { Button } from "../components/ui";
import { rememberDeviceAccount } from "../device-accounts";

export function AuthPage({ needsSetup }: { needsSetup: boolean }) {
  const [displayName, setDisplayName] = useState(() =>
    needsSetup ? "" : window.localStorage.getItem("prive_login_name") || "",
  );
  const [password, setPassword] = useState("");
  const [rememberLogin, setRememberLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await api<{ user: User }>(
        needsSetup ? "/api/auth/setup" : "/api/auth/login",
        {
        method: "POST",
        body: JSON.stringify(
          needsSetup
            ? { displayName, password }
            : { displayName, password, rememberLogin },
        ),
        },
      );
      rememberDeviceAccount(result.user, {
        persistent: needsSetup || rememberLogin,
      });
      if (!needsSetup) {
        if (rememberLogin)
          window.localStorage.setItem("prive_login_name", displayName.trim());
        else window.localStorage.removeItem("prive_login_name");
      }
      window.location.replace("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "操作失败");
      setLoading(false);
    }
  }
  return (
    <main className="auth-page">
      <aside className="auth-stitch-visual" aria-hidden="true">
        <img
          src="/theme-stitch/login-hero.webp"
          alt=""
          onError={(event) => {
            event.currentTarget.hidden = true;
          }}
        />
        <span>OHANA · 每一天都算数</span>
      </aside>
      <section className="auth-card">
        <div className="auth-logo">
          P<span>✿</span>
        </div>
        <p className="eyebrow">PRIVE PERSONAL WORKSPACE</p>
        <h1>{needsSetup ? "欢迎来到你的生活工作台" : "欢迎回来"}</h1>
        <p className="auth-sub">旅行、学习、计划与世界，都在这里温柔地汇合。</p>
        <form onSubmit={submit}>
          <div className="field">
            <label>{needsSetup ? "我该怎么称呼你" : "账号昵称"}</label>
            <div className={needsSetup ? undefined : "input-icon"}>
              {!needsSetup && <UserRound size={17} />}
              <input
                className={needsSetup ? "input" : undefined}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={needsSetup ? "例如：小椰" : "输入你的账号昵称"}
                autoComplete={needsSetup ? "name" : "username"}
                required
              />
            </div>
          </div>
          <div className="field">
            <label>{needsSetup ? "设置专属密码" : "工作台密码"}</label>
            <div className="input-icon">
              <LockKeyhole size={17} />
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                minLength={needsSetup ? 6 : 1}
                placeholder={needsSetup ? "至少6位，只属于你" : "输入密码"}
                autoComplete={needsSetup ? "new-password" : "current-password"}
              />
            </div>
          </div>
          {!needsSetup && (
            <label className="auth-remember">
              <input
                type="checkbox"
                checked={rememberLogin}
                onChange={(event) => setRememberLogin(event.target.checked)}
              />
              <span>
                <b>15 天内保持登录</b>
                <small>保存安全会话，不在页面中存储明文密码</small>
              </span>
            </label>
          )}
          {error && <div className="error-box">{error}</div>}
          <Button loading={loading} type="submit">
            <Sparkles size={17} />
            {needsSetup ? "开启我的工作台" : "进入工作台"}
          </Button>
        </form>
        <div className="auth-safe">
          <Heart size={14} /> 私人空间 · 数据保存在你的服务器
        </div>
      </section>
    </main>
  );
}
